import type { PaginationState } from '@tanstack/react-table'
import { StateCreator, createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import {
  type ConfirmationStatusFilter,
  DEFAULT_CONFIRMATION_STATUS_FILTERS,
} from '@/components/library-v2/lib'

import { IS_PROD_LIKE_ENVIRONMENT } from '@/constants/env'

import type { UUID } from '@/types/Id'

// region store types

export interface PeopleStoreFilterState {
  roles: string[]
  department_ids: UUID[]
  company_ids: UUID[]
  agency_ids: UUID[]
  confirmationStatuses: ConfirmationStatusFilter[]
  internalStatuses: string[] // 'internal' | 'external'
  relationshipStatuses: string[] // From RELATIONSHIP_STATUS_OPTIONS
}

export interface PeopleStoreState {
  filters: PeopleStoreFilterState
  setFilters: (filters: Partial<PeopleStoreFilterState>) => void
  addRelationshipStatusFilter: (status: string) => void
  resetFilters: () => void

  searchQuery: string
  setSearchQuery: (query: string) => void

  selectedPersonIds: UUID[]
  setSelectedPersonIds: (ids: UUID[]) => void

  lastSelectedRowId: string | null
  setLastSelectedRowId: (id: string | null) => void

  pagination: PaginationState
  setPagination: (pagination: PaginationState) => void

  deleteDialogOpen: boolean
  setDeleteDialogOpen: (open: boolean) => void

  mergeDialogOpen: boolean
  setMergeDialogOpen: (open: boolean) => void

  confirmDialogOpen: boolean
  setConfirmDialogOpen: (open: boolean) => void

  createDialogOpen: boolean
  setCreateDialogOpen: (open: boolean) => void

  importDialogOpen: boolean
  setImportDialogOpen: (open: boolean) => void
}

const makeInitStore = (): StateCreator<PeopleStoreState> => (set, get) => ({
  filters: {
    roles: [],
    department_ids: [],
    company_ids: [],
    agency_ids: [],
    confirmationStatuses: [...DEFAULT_CONFIRMATION_STATUS_FILTERS],
    internalStatuses: [],
    relationshipStatuses: [],
  },
  setFilters: (filters) => {
    const existingFilters = get().filters
    const newFilters = { ...existingFilters, ...filters }
    set({ filters: newFilters })
  },
  addRelationshipStatusFilter: (status) => {
    const existing = get().filters.relationshipStatuses
    if (!existing.includes(status)) {
      set({
        filters: {
          ...get().filters,
          relationshipStatuses: [...existing, status],
        },
      })
    }
  },
  resetFilters: () =>
    set({
      filters: {
        roles: [],
        department_ids: [],
        company_ids: [],
        agency_ids: [],
        confirmationStatuses: [...DEFAULT_CONFIRMATION_STATUS_FILTERS],
        internalStatuses: [],
        relationshipStatuses: [],
      },
    }),

  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  selectedPersonIds: [],
  setSelectedPersonIds: (ids) => set({ selectedPersonIds: ids }),

  lastSelectedRowId: null,
  setLastSelectedRowId: (id) => set({ lastSelectedRowId: id }),

  pagination: { pageIndex: 0, pageSize: 50 },
  setPagination: (pagination) => set({ pagination }),

  deleteDialogOpen: false,
  setDeleteDialogOpen: (open) => set({ deleteDialogOpen: open }),

  mergeDialogOpen: false,
  setMergeDialogOpen: (open) => set({ mergeDialogOpen: open }),

  confirmDialogOpen: false,
  setConfirmDialogOpen: (open) => set({ confirmDialogOpen: open }),

  createDialogOpen: false,
  setCreateDialogOpen: (open) => set({ createDialogOpen: open }),

  importDialogOpen: false,
  setImportDialogOpen: (open) => set({ importDialogOpen: open }),
})

// end region store types

// region store

export type PeopleStore = ReturnType<typeof createPeopleStore>

export const createPeopleStore = () => {
  if (IS_PROD_LIKE_ENVIRONMENT) {
    return createStore<PeopleStoreState, [['zustand/immer', never]]>(
      immer<PeopleStoreState>(makeInitStore()),
    )
  }
  return createStore<
    PeopleStoreState,
    [['zustand/devtools', never], ['zustand/immer', never]]
  >(devtools(immer<PeopleStoreState>(makeInitStore())))
}

// end region store
